import * as React from 'react'
import { Check, ChevronDown, ListFilter, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface FilterMenuOption {
  /** The value carried in state and sent to the API. */
  value: string
  label: string
  /**
   * True for an option that cannot be held alongside the others — "Not set"
   * beside a tag, say, which asks two contradictory things at once. Picking one
   * drops everything else rather than quietly returning nothing.
   */
  exclusive?: boolean
}

/**
 * A multi-select filter: a dropdown holding the options, the chosen ones as
 * chips beside it.
 *
 * Belongs in a `Table`'s `actions`, beside whatever the table's own button is.
 * That is where this app puts filters, and it only works because the header
 * band survives an empty result — see `TableEmptyRow`. A filter rendered inside
 * the table body would disappear at exactly the moment somebody needs to widen
 * it.
 *
 * The options live behind a trigger rather than spread across the band because
 * a list of them is open-ended, and a dozen laid out in a row pushes the
 * table's own controls off the end. What stays on show is what was picked.
 */
export function FilterMenu({
  label,
  options,
  selected,
  onToggle,
  onClear,
  className,
}: {
  /** Names the property being filtered — "Tags", "Status". */
  label: string
  options: readonly FilterMenuOption[]
  selected: readonly string[]
  onToggle: (value: string) => void
  onClear: () => void
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  if (options.length === 0) return null
  const labelFor = (value: string): string => options.find((o) => o.value === value)?.label ?? value

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-[30px] items-center gap-1.5 rounded-md border px-2.5 text-control font-medium outline-none transition-colors focus-visible:focus-ds',
              selected.length > 0
                ? 'border-transparent bg-accent-soft text-accent-ink'
                : 'border-line-control bg-surface text-ink-muted shadow-xs hover:bg-surface-hover hover:text-ink',
            )}
          >
            <ListFilter className="h-3.5 w-3.5 shrink-0 opacity-70" />
            <span>{label}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        {/* Stays open while options are picked: choosing several is the point. */}
        <PopoverContent align="end" className="w-60 p-1">
          {/* Scrolls rather than growing — the list is as long as the data has
              made it, and a popover taller than the window cannot be closed. */}
          <div className="max-h-64 overflow-y-auto">
            {options.map((option) => {
              const on = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onToggle(option.value)}
                  className={cn(
                    'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-sm outline-none transition-colors focus-visible:shadow-[inset_0_0_0_2px_var(--ds-focus)]',
                    on ? 'bg-accent-soft font-medium text-accent-ink' : 'text-ink hover:bg-surface-hover',
                  )}
                >
                  <span className="grid h-4 w-4 shrink-0 place-items-center">
                    {on ? <Check className="h-3.5 w-3.5" /> : null}
                  </span>
                  <span className="min-w-0 truncate">{option.label}</span>
                </button>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>

      {selected.map((value) => (
        <Badge key={value} className="gap-1 pr-1">
          {labelFor(value)}
          <button
            type="button"
            onClick={() => onToggle(value)}
            aria-label={`Stop filtering by ${labelFor(value)}`}
            className="rounded-sm p-0.5 text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:focus-ds"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {selected.length > 1 ? (
        <button
          type="button"
          onClick={onClear}
          className="rounded-sm px-1.5 py-0.5 text-xs text-ink-muted outline-none transition-colors hover:text-ink focus-visible:focus-ds"
        >
          Clear
        </button>
      ) : null}
    </div>
  )
}

/**
 * Apply a pick to a selection, honoring any exclusive option.
 *
 * Shared so every filter answers the question the same way, rather than each
 * page inventing its own rule about what happens when two options contradict.
 */
export function toggleFilterValue(
  current: readonly string[],
  value: string,
  options: readonly FilterMenuOption[],
): string[] {
  if (current.includes(value)) return current.filter((v) => v !== value)
  const exclusive = new Set(options.filter((o) => o.exclusive === true).map((o) => o.value))
  if (exclusive.has(value)) return [value]
  return [...current.filter((v) => !exclusive.has(v)), value]
}
